"use client";

import { composeRefs } from "@radix-ui/react-compose-refs";
import React from "react";
import { withRefProp } from "../../../utils/withRefProp";
import Button from "../../common/Button/Button";
import Icon from "../../common/Icon/Icon";
import TextInput, { type TextInputProps } from "../TextInput/TextInput";
import Calendar from "./Calendar";
import styles from "./DateTimeInput.module.scss";
import { PickerPopover } from "./PickerPopover";
import {
  type DateOrder,
  formatDate,
  getDatePlaceholder,
  getLocaleDateOrder,
  parseDate,
  parsePastedDateTime,
  startOfDay,
} from "./dateTimeFormat";
import { useCommittedText } from "./useCommittedText";

const NOW = "now" as const;

export interface DateInputProps
  extends Omit<TextInputProps, "value" | "onValueChange" | "onChange" | "type" | "clearable" | "onClear"> {
  /** Only the calendar day is used; the time of day on the value is ignored. */
  value?: Date | null;
  /** Emits the picked day at local midnight, or null when cleared. */
  onValueChange?: (value: Date | null) => void;
  /** BCP 47 locale for the day/month order. Defaults to the browser locale. */
  locale?: string;
  minDate?: Date;
  maxDate?: Date;
  disabledDates?: Date[];
  clearable?: boolean;
  onClear?: () => void;
  /** Show a "Now" toggle and accept "now" as typed input. */
  allowNow?: boolean;
  now?: boolean;
  onNowChange?: (now: boolean) => void;
  /** Where the calendar icon sits: "start" (default), "end", or "hidden". The icon is visual only; clicking the input or ArrowDown opens the calendar. */
  pickerIconPosition?: "start" | "end" | "hidden";
}

const isOutOfRange = (date: Date, minDate?: Date, maxDate?: Date, disabledDates?: Date[]) =>
  (minDate !== undefined && date < startOfDay(minDate)) ||
  (maxDate !== undefined && date > startOfDay(maxDate)) ||
  Boolean(disabledDates?.some((disabled) => disabled.toDateString() === date.toDateString()));

const DateInput = withRefProp(
  ({
    value = null,
    onValueChange,
    locale,
    minDate,
    maxDate,
    disabledDates,
    clearable,
    onClear,
    clearAriaLabel,
    allowNow = false,
    now = false,
    onNowChange,
    pickerIconPosition = "start",
    disabled,
    error,
    placeholder,
    startAdornment,
    endAdornment,
    onBlur,
    onKeyDown,
    onClick,
    onPaste,
    ref,
    ...otherProps
  }: DateInputProps) => {
    const inputRef = React.useRef<HTMLInputElement | null>(null);
    const [open, setOpen] = React.useState(false);
    const [focusCalendar, setFocusCalendar] = React.useState(false);
    const [month, setMonth] = React.useState<Date>(value ?? new Date());
    const order: DateOrder = React.useMemo(() => getLocaleDateOrder(locale), [locale]);

    const committed: Date | typeof NOW | null = now ? NOW : value;
    const text = useCommittedText<Date | typeof NOW>({
      value: committed,
      format: (next) => (next === NOW ? NOW : formatDate(next, order)),
      parse: (input) => {
        if (allowNow && input.trim().toLowerCase() === NOW) return NOW;
        const parsed = parseDate(input, order);
        return parsed && !isOutOfRange(parsed, minDate, maxDate, disabledDates) ? parsed : null;
      },
      onCommit: (next) => {
        if (next === NOW) {
          onNowChange?.(true);
          return;
        }
        if (now) onNowChange?.(false);
        onValueChange?.(next);
      },
    });

    const valueMonthKey = value ? `${value.getFullYear()}-${value.getMonth()}` : "";
    // Follow the value only when it moves to a different month.
    React.useEffect(() => {
      if (value) setMonth(value);
    }, [valueMonthKey]);

    const openPicker = (withFocus: boolean) => {
      if (disabled) return;
      setFocusCalendar(withFocus);
      setOpen(true);
    };
    const closePicker = () => setOpen(false);

    const selectDay = (day: Date | undefined) => {
      if (!day) return;
      text.reset();
      if (now) onNowChange?.(false);
      onValueChange?.(startOfDay(day));
      closePicker();
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      onKeyDown?.(event);
      if (event.defaultPrevented) return;
      if (event.key === "ArrowDown") {
        event.preventDefault();
        openPicker(true);
      } else if (event.key === "Escape" && open) {
        event.preventDefault();
        event.stopPropagation();
        closePicker();
      } else if (event.key === "Enter") {
        // Only swallow Enter while the picker is open, so Enter still submits a surrounding form.
        if (open) event.preventDefault();
        text.commit();
        closePicker();
      }
    };

    // A pasted timestamp ("2026-12-25T10:30:00.000Z") sets its day. Plain dates paste as normal text.
    const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
      onPaste?.(event);
      if (event.defaultPrevented) return;
      const pastedText = event.clipboardData.getData("text");
      if (parseDate(pastedText, order)) return;
      const pasted = parsePastedDateTime(pastedText, order);
      if (!pasted || isOutOfRange(pasted.date, minDate, maxDate, disabledDates)) return;
      event.preventDefault();
      text.reset();
      if (now) onNowChange?.(false);
      onValueChange?.(pasted.date);
    };

    const toggleNow = () => {
      text.reset();
      if (now) {
        onNowChange?.(false);
        onValueChange?.(null);
      } else {
        onNowChange?.(true);
      }
    };

    const handleClear = () => {
      text.reset();
      if (now) onNowChange?.(false);
      onValueChange?.(null);
      onClear?.();
    };

    // Visual cue only: not a button and not focusable, so it never takes focus (e.g. a dialog's
    // initial focus). Keyboard users open the calendar with ArrowDown; a pointer press on the icon
    // focuses the input and toggles the calendar as a convenience.
    const pickerIcon = (
      <span
        className={styles.pickerIcon}
        data-picker-icon={pickerIconPosition}
        aria-hidden
        onMouseDown={(event) => {
          event.preventDefault();
          if (disabled) return;
          inputRef.current?.focus();
          if (open) closePicker();
          else openPicker(false);
        }}
      >
        <Icon name="date-range" />
      </span>
    );

    return (
      <PickerPopover
        open={open}
        onOpenChange={setOpen}
        focusContent={focusCalendar}
        onCloseFocus={() => {
          if (focusCalendar) inputRef.current?.focus();
        }}
        anchor={
          <div
            className={styles.anchor}
            data-full-width={otherProps.fullWidth ? "true" : "false"}
          >
            <TextInput
              {...otherProps}
              ref={composeRefs(inputRef, ref)}
              value={text.text}
              onValueChange={(next) => text.change(next ?? "")}
              onClick={(event) => {
                onClick?.(event);
                if (open) closePicker();
                else openPicker(false);
              }}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
                text.commit();
                onBlur?.(event);
              }}
              disabled={disabled}
              error={error || text.error}
              aria-invalid={error || text.error || undefined}
              aria-haspopup="dialog"
              aria-expanded={open}
              placeholder={placeholder ?? getDatePlaceholder(order)}
              clearable={clearable}
              onClear={handleClear}
              clearAriaLabel={clearAriaLabel}
              startAdornment={
                pickerIconPosition === "start" || startAdornment ? (
                  <>
                    {pickerIconPosition === "start" ? pickerIcon : null}
                    {startAdornment}
                  </>
                ) : undefined
              }
              endAdornment={
                <>
                  {endAdornment}
                  {allowNow ? (
                    <Button
                      type="button"
                      size="xs"
                      variant="outline"
                      intent="secondary"
                      label="Now"
                      selected={now}
                      aria-pressed={now}
                      onClick={toggleNow}
                      disabled={disabled}
                    />
                  ) : null}
                  {pickerIconPosition === "end" ? pickerIcon : null}
                </>
              }
            />
          </div>
        }
      >
        <Calendar
          mode="single"
          autoFocus={focusCalendar}
          month={month}
          onMonthChange={setMonth}
          selected={value ?? undefined}
          onSelect={selectDay}
          startMonth={minDate}
          endMonth={maxDate}
          disabled={[
            ...(minDate ? [{ before: minDate }] : []),
            ...(maxDate ? [{ after: maxDate }] : []),
            ...(disabledDates ?? []),
          ]}
        />
      </PickerPopover>
    );
  },
);

export default DateInput;
